/**
 * Chats de WhatsApp exportados a mano («Exportar chat → Sin archivos»).
 *
 * Lee el .txt que saca la app (Android o iPhone, en español o en inglés) y lo
 * convierte en mensajes para guardar en la ficha del lead con
 * `crm_whatsapp_importar` (supabase/migrations/202611040003_whatsapp_exportado.sql).
 * Mismas reglas que `dominio/whatsapp_exportado.py` del MCP local, que lee lo
 * mismo cuando el chat llega desde el ordenador.
 *
 * Formatos que entiende (las horas son de Madrid, la del móvil de Juan):
 *   Android  04/11/26, 18:30 - Lola: ¿Tenéis libre el 12 de junio?
 *            04/11/2026 18:30 - …   ·   4/11/26, 6:30 p. m. - …
 *   iPhone   [04/11/26, 18:30:12] Lola: ¿Tenéis libre…?
 * Un mensaje puede ocupar varias líneas. Las líneas sin autor (el aviso del
 * cifrado, «cambió su código de seguridad»…) y los mensajes eliminados no se
 * guardan; «<Multimedia omitido>», «audio omitido», «IMG-….jpg (archivo
 * adjunto)» y compañía se guardan como nota de voz, foto, etc., sin el archivo.
 */
import type { WhatsappMensaje } from "./types";

export type TipoMensajeExportado = WhatsappMensaje["tipo"];

export interface MensajeExportado {
  /** Instante en UTC (ISO), leído de la hora de Madrid del chat. */
  enviado_at: string;
  autor: string;
  tipo: TipoMensajeExportado;
  texto: string | null;
}

export interface ChatExportado {
  /** Con quién es el chat según el nombre del archivo («Chat de WhatsApp con Lola»). */
  pareja: string | null;
  /** Quién escribe, por orden de aparición. */
  autores: string[];
  /** Del más antiguo al más reciente. */
  mensajes: MensajeExportado[];
  /** Avisos del sistema y mensajes eliminados que no se guardan. */
  descartados: number;
}

/** Lo que recibe `crm_whatsapp_importar`. */
export interface MensajeAImportar {
  enviado_at: string;
  direccion: "ENTRANTE" | "SALIENTE";
  tipo: TipoMensajeExportado;
  texto: string | null;
  autor: string;
}

/* --- Limpiar -------------------------------------------------------------- */

// Marcas de dirección (U+200E…) que WhatsApp mete delante de nombres y avisos,
// y el BOM del principio del archivo.
const MARCAS_INVISIBLES = /[\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g;
// Espacios raros: el estrecho de «6:30 p. m.» (U+202F) y el duro (U+00A0).
const ESPACIOS_RAROS = /[\u202f\u00a0\u2009]/g;

function limpiar(linea: string): string {
  return linea.replace(MARCAS_INVISIBLES, "").replace(ESPACIOS_RAROS, " ");
}

/* --- Cabecera de cada mensaje ------------------------------------------- */

const FECHA = String.raw`(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})`;
const HORA = String.raw`(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([ap])\.?\s?m\.?)?`;
// Android: «fecha, hora - resto»; iPhone: «[fecha, hora] resto».
const CABECERA = new RegExp(
  String.raw`^(?:\[${FECHA},?\s+${HORA}\]\s*|${FECHA},?\s+${HORA}\s+[-–]\s+)(.*)$`,
  "i",
);
const AUTOR = /^([^:\n]{1,80}?): ([\s\S]*)$/;

interface Bruto {
  a: number;
  b: number;
  anio: number;
  hora: number;
  minuto: number;
  segundo: number;
  meridiano: "a" | "p" | null;
  cuerpo: string;
}

function leerCabecera(linea: string): Bruto | null {
  const m = CABECERA.exec(linea);
  if (!m) return null;
  // Los grupos 1-7 son de la forma iPhone y los 8-14 de la Android.
  const g = m[1] !== undefined ? m.slice(1, 8) : m.slice(8, 15);
  const anio = Number(g[2]);
  return {
    a: Number(g[0]),
    b: Number(g[1]),
    anio: anio < 100 ? 2000 + anio : anio,
    hora: Number(g[3]),
    minuto: Number(g[4]),
    segundo: g[5] ? Number(g[5]) : 0,
    meridiano: g[6] ? (g[6].toLowerCase() as "a" | "p") : null,
    cuerpo: m[15] ?? "",
  };
}

/* --- Hora de Madrid -------------------------------------------------------- */

const partesMadrid = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Madrid",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** Cuánto va Madrid por delante de UTC en ese instante, en milisegundos. */
function desfaseMadrid(instante: number): number {
  const p = Object.fromEntries(
    partesMadrid.formatToParts(new Date(instante)).map((x) => [x.type, x.value]),
  );
  return (
    Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) -
    Math.floor(instante / 1000) * 1000
  );
}

/** La hora de pared de Madrid como instante (en la hora que se repite en octubre, la segunda). */
export function instanteMadrid(
  anio: number,
  mes: number,
  dia: number,
  hora: number,
  minuto: number,
  segundo = 0,
): Date {
  const pared = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo);
  const aprox = pared - desfaseMadrid(pared);
  return new Date(pared - desfaseMadrid(aprox));
}

/* --- Qué es cada mensaje ------------------------------------------------- */

// Avisos que no escribió nadie, aunque en iPhone salgan con el nombre del chat.
const AVISOS_DEL_SISTEMA = [
  /cifrad[oa]s de extremo a extremo/i,
  /end-to-end encrypted/i,
  /c[oó]digo de seguridad/i,
  /security code/i,
  /^(esperando (este|el) mensaje|waiting for this message)/i,
  /mensajes temporales|disappearing messages/i,
  /^(llamada|videollamada)( de (voz|v[ií]deo))? perdida/i,
  /^missed (voice|video) call/i,
  /^(esta empresa|this business) /i,
  /cuenta (de empresa|empresarial)|business account/i,
  /^(bloqueaste|desbloqueaste) a este contacto|^you (un)?blocked this contact/i,
  /cambi[oó] (su|de) n[uú]mero|changed (their|to a new) (phone )?number/i,
  /^(creaste|cre[oó]) el grupo|created group/i,
];

const ELIMINADO =
  /^(se elimin[oó] este mensaje|eliminaste este mensaje|este mensaje (fue|se) eliminad[oa]|you deleted this message|this message was deleted)\.?$/i;

const EDITADO =
  /\s*<(se edit[oó] este mensaje|este mensaje se edit[oó]|mensaje editado|this message was edited)\.?>\s*$/i;

const OMITIDOS: [RegExp, TipoMensajeExportado][] = [
  [/^<?(multimedia omitido|media omitted)>?$/i, "other"],
  [/^(audio|nota de voz|mensaje de voz) omitid[oa]$|^audio omitted$/i, "audio"],
  [/^(imagen|foto) omitida$|^image omitted$/i, "image"],
  [/^(v[ií]deo|gif) omitido$|^(video|gif) omitted$/i, "video"],
  [/^sticker omitido$|^sticker omitted$/i, "sticker"],
  [/^documento omitido$|^document omitted$/i, "document"],
  [/^tarjeta de contacto omitida$|^contact card omitted$/i, "other"],
];

// iPhone: «<adjunto: 00000012-PHOTO-2026-11-04-18-30-12.jpg>»; Android con
// archivos: «PTT-20261104-WA0003.opus (archivo adjunto)». Detrás, el pie de foto.
const ADJUNTO_IPHONE = /^<(?:adjunto|attached): ([^>]+)>\s*([\s\S]*)$/i;
const ADJUNTO_ANDROID = /^(\S+\.\w{2,5}) \((?:archivo adjunto|file attached)\)\s*([\s\S]*)$/i;
const UBICACION = /^(?:ubicaci[oó]n|location): (https?:\/\/\S+)\s*$/i;
const UBICACION_EN_VIVO = /^(ubicaci[oó]n en tiempo real|live location)/i;

function tipoDeArchivo(nombre: string): TipoMensajeExportado {
  if (/^STK-|STICKER|\.webp$/i.test(nombre)) return "sticker";
  if (/^(PTT|AUD)-|AUDIO|\.(opus|ogg|m4a|mp3|aac|amr)$/i.test(nombre)) return "audio";
  if (/^IMG-|PHOTO|\.(jpe?g|png|heic|gif)$/i.test(nombre)) return "image";
  if (/^VID-|VIDEO|\.(mp4|3gp|mov|avi|mkv)$/i.test(nombre)) return "video";
  return "document";
}

type Contenido = { tipo: TipoMensajeExportado; texto: string | null } | null;

/** Qué es un mensaje por su texto; null si no se guarda. */
export function contenidoDelMensaje(cuerpo: string): Contenido {
  const texto = cuerpo.replace(EDITADO, "").trim();
  if (!texto || texto === "null" || ELIMINADO.test(texto)) return null;
  if (AVISOS_DEL_SISTEMA.some((aviso) => aviso.test(texto))) return null;
  for (const [patron, tipo] of OMITIDOS) if (patron.test(texto)) return { tipo, texto: null };
  const adjunto = ADJUNTO_IPHONE.exec(texto) ?? ADJUNTO_ANDROID.exec(texto);
  if (adjunto) {
    const tipo = tipoDeArchivo(adjunto[1].trim());
    const pie = adjunto[2].trim() || null;
    return { tipo, texto: tipo === "document" ? (pie ?? adjunto[1].trim()) : pie };
  }
  const ubicacion = UBICACION.exec(texto);
  if (ubicacion) return { tipo: "location", texto: ubicacion[1] };
  if (UBICACION_EN_VIVO.test(texto)) return { tipo: "location", texto: null };
  return { tipo: "text", texto };
}

const ETIQUETA_TIPO: Record<TipoMensajeExportado, string> = {
  text: "Mensaje",
  audio: "Nota de voz",
  image: "Foto",
  video: "Vídeo",
  document: "Documento",
  sticker: "Sticker",
  location: "Ubicación",
  reaction: "Reacción",
  other: "Archivo",
};

/** Cómo se lee en la vista previa: el texto o qué era («Nota de voz (no viene…)»). */
export function resumenDelMensaje(tipo: TipoMensajeExportado, texto: string | null): string {
  if (tipo === "text") return texto ?? "";
  return texto
    ? `${ETIQUETA_TIPO[tipo]}: ${texto}`
    : `${ETIQUETA_TIPO[tipo]} (no viene en el chat exportado)`;
}

/* --- Leer el archivo ------------------------------------------------------- */

/** Lee un chat exportado. `nombreArchivo` (o el título que manda Android) dice con quién es. */
export function leerChatExportado(contenido: string, nombreArchivo?: string | null): ChatExportado {
  const brutos: Bruto[] = [];
  for (const linea of contenido.split(/\r\n|\r|\n/).map(limpiar)) {
    const cabecera = leerCabecera(linea);
    if (cabecera) brutos.push(cabecera);
    else if (brutos.length > 0) brutos[brutos.length - 1].cuerpo += `\n${linea}`;
  }

  // Día/mes (España) salvo que alguna fecha solo cuadre como mes/día (móvil en inglés de EE. UU.).
  const mesDia = !brutos.some((b) => b.a > 12) && brutos.some((b) => b.b > 12);

  const autores: string[] = [];
  const mensajes: MensajeExportado[] = [];
  let descartados = 0;
  for (const b of brutos) {
    const conAutor = AUTOR.exec(b.cuerpo);
    const contenidoMensaje = conAutor ? contenidoDelMensaje(conAutor[2]) : null;
    if (!conAutor || !contenidoMensaje) {
      descartados++;
      continue;
    }
    const autor = conAutor[1].trim();
    if (!autores.includes(autor)) autores.push(autor);
    const hora = b.meridiano ? (b.hora % 12) + (b.meridiano === "p" ? 12 : 0) : b.hora;
    const [dia, mes] = mesDia ? [b.b, b.a] : [b.a, b.b];
    const instante = instanteMadrid(b.anio, mes, dia, hora, b.minuto, b.segundo);
    mensajes.push({ enviado_at: instante.toISOString(), autor, ...contenidoMensaje });
  }
  return { pareja: parejaDelNombre(nombreArchivo), autores, mensajes, descartados };
}

/** ¿Parece un chat exportado? Para no tomar por chat cualquier texto compartido. */
export function pareceChatExportado(contenido: string): boolean {
  return contenido.split(/\r\n|\r|\n/, 20).some((linea) => leerCabecera(limpiar(linea)) !== null);
}

const NOMBRES_DE_CHAT = [
  /^chat de whatsapp con (.+)$/i,
  /^chat de whatsapp - (.+)$/i,
  /^whatsapp chat with (.+)$/i,
  /^whatsapp chat - (.+)$/i,
  /^conversa do whatsapp com (.+)$/i,
];

/** «Chat de WhatsApp con Lola.txt» → «Lola». */
export function parejaDelNombre(nombre: string | null | undefined): string | null {
  if (!nombre) return null;
  const limpio = limpiar(nombre)
    .replace(/^.*[\\/]/, "")
    .replace(/\.(txt|zip)$/i, "")
    .replace(/\s*\(\d+\)$/, "")
    .trim();
  for (const patron of NOMBRES_DE_CHAT) {
    const m = patron.exec(limpio);
    if (m) return m[1].trim() || null;
  }
  return null;
}

/* --- Quién es quién ------------------------------------------------------ */

/** Minúsculas, sin tildes ni signos: «José Mª» → «jose m». */
export function normalizarNombre(nombre: string): string {
  return limpiar(nombre)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** Las cifras si el «nombre» es un teléfono («+34 600 11 22 33»), si no null. */
export function pareceTelefono(nombre: string | null | undefined): string | null {
  if (!nombre) return null;
  const limpio = limpiar(nombre).trim();
  const cifras = limpio.replace(/\D/g, "");
  return /^\+?[\d\s().-]+$/.test(limpio) && cifras.length >= 9 ? cifras : null;
}

/** El mismo autor aunque cambien tildes, mayúsculas o el formato del teléfono. */
export function mismoAutor(a: string, b: string): boolean {
  const [ta, tb] = [pareceTelefono(a), pareceTelefono(b)];
  if (ta && tb) return ta.slice(-9) === tb.slice(-9);
  return normalizarNombre(a) === normalizarNombre(b);
}

export interface ParejaDelChat {
  /** El autor que es la pareja; lo demás lo escribió Juan. */
  autor: string | null;
  /** false: es una suposición y Juan debería confirmarla. */
  segura: boolean;
}

/**
 * Qué autor es la pareja: el que se llama como el chat. Si el nombre del
 * archivo no lo dice, se supone el primero que escribe (normalmente el lead
 * escribe primero) y se pide confirmarlo.
 */
export function parejaDelChat(chat: ChatExportado): ParejaDelChat {
  const porNombre = chat.pareja ? chat.autores.find((a) => mismoAutor(a, chat.pareja!)) : undefined;
  if (porNombre) return { autor: porNombre, segura: true };
  return { autor: chat.autores[0] ?? null, segura: false };
}

/**
 * Sin nombre de archivo (texto pegado o leído de WhatsApp Web), la pareja es
 * el único autor que se llama como el lead o tiene su teléfono; null si no
 * hay uno claro.
 */
export function parejaSegunLead(
  autores: string[],
  lead: { nombre: string; telefono: string | null },
): string | null {
  const telefono = lead.telefono?.replace(/\D/g, "").slice(-9) || null;
  const candidatos = autores.filter((autor) => {
    const suyo = pareceTelefono(autor);
    if (suyo) return telefono !== null && suyo.slice(-9) === telefono;
    const comunes = palabras(autor).filter((p) => palabras(lead.nombre).includes(p));
    return comunes.length > 0;
  });
  return candidatos.length === 1 ? candidatos[0] : null;
}

/** Los mensajes listos para `crm_whatsapp_importar`: lo de la pareja entra, lo demás es de Juan. */
export function mensajesAImportar(chat: ChatExportado, pareja: string | null): MensajeAImportar[] {
  return chat.mensajes.map((m) => ({
    enviado_at: m.enviado_at,
    direccion: pareja !== null && m.autor === pareja ? "ENTRANTE" : "SALIENTE",
    tipo: m.tipo,
    texto: m.texto,
    autor: m.autor,
  }));
}

/**
 * Tandas para `crm_whatsapp_importar` sin partir nunca un minuto entre dos:
 * los mensajes iguales del mismo minuto se cuentan dentro de la tanda.
 */
export function enTandas<T extends { enviado_at: string }>(mensajes: T[], tamano = 1000): T[][] {
  const tandas: T[][] = [];
  let actual: T[] = [];
  for (const m of mensajes) {
    const ultimo = actual[actual.length - 1];
    if (actual.length >= tamano && minuto(ultimo.enviado_at) !== minuto(m.enviado_at)) {
      tandas.push(actual);
      actual = [];
    }
    actual.push(m);
  }
  if (actual.length > 0) tandas.push(actual);
  return tandas;
}

function minuto(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 60_000);
}

/* --- Sugerir el lead ------------------------------------------------------- */

// Palabras que no ayudan a casar («Lola y Pablo», «Lola boda junio»).
const PALABRAS_VACIAS = new Set([
  "y",
  "e",
  "de",
  "del",
  "la",
  "el",
  "los",
  "las",
  "boda",
  "novia",
  "novio",
  "novios",
]);

function palabras(nombre: string): string[] {
  return normalizarNombre(nombre)
    .split(" ")
    .filter((p) => p && !PALABRAS_VACIAS.has(p));
}

export interface LeadCandidato {
  id: string;
  name: string;
  stage: string;
  created_at: string;
}

/**
 * Los leads que se llaman como el chat, del más probable al menos: cuántas
 * palabras del nombre del chat están en el del lead (sin tildes ni
 * mayúsculas); a igualdad, primero los que no están perdidos y los recientes.
 */
export function sugerirLeads<L extends LeadCandidato>(pareja: string | null, leads: L[]): L[] {
  const buscadas = pareja ? palabras(pareja) : [];
  if (buscadas.length === 0) return [];
  const puntuados = leads
    .map((lead) => {
      const suyas = palabras(lead.name);
      const comunes = buscadas.filter((p) => suyas.includes(p)).length;
      const exacto = normalizarNombre(lead.name) === normalizarNombre(pareja!) ? 1 : 0;
      return { lead, puntos: comunes / buscadas.length + exacto };
    })
    .filter((p) => p.puntos > 0);
  puntuados.sort(
    (x, y) =>
      y.puntos - x.puntos ||
      Number(x.lead.stage === "PERDIDO") - Number(y.lead.stage === "PERDIDO") ||
      y.lead.created_at.localeCompare(x.lead.created_at),
  );
  return puntuados.map((p) => p.lead);
}
